// Equation Box generator. Produces a jar of number and operator gems plus a target,
// with at least one known solution. Rungs 1..10 widen numbers and operators.

import { int, pick, shuffle, type Rng } from '@kit/rng';
import { evaluate, type Tok } from './expr';

export interface Jar {
  numbers: number[];          // number gems (may repeat)
  ops: ('+' | '-' | '*' | '/')[];
  parens: boolean;            // are bracket gems available
  x?: { value: number; clue: string }; // unknown gem with a side equation
  target: number;
  solution: Tok[];            // one known answer, for hints
  minGems: number;            // numbers needed by the known solution
}

export interface Rules { ops: ('+' | '-' | '*' | '/')[]; max: number; parens: boolean; x: boolean; targetMax: number; numCount: number }

export function rulesFor(level: number): Rules {
  const L = Math.max(1, Math.min(10, level));
  if (L <= 2) return { ops: ['+', '-'], max: 10, parens: false, x: false, targetMax: 20, numCount: 6 };
  if (L <= 4) return { ops: ['+', '-'], max: 20, parens: false, x: false, targetMax: 50, numCount: 7 };
  if (L <= 6) return { ops: ['+', '-', '*'], max: 12, parens: false, x: false, targetMax: 60, numCount: 7 };
  if (L <= 8) return { ops: ['+', '-', '*', '/'], max: 12, parens: true, x: false, targetMax: 100, numCount: 8 };
  return { ops: ['+', '-', '*', '/'], max: 15, parens: true, x: true, targetMax: 120, numCount: 8 };
}

/** Build a solution expression first, then seed the jar with its parts plus distractors. */
export function makeJar(level: number, rng: Rng): Jar {
  const r = rulesFor(level);
  for (let attempt = 0; attempt < 200; attempt++) {
    const terms = level <= 2 ? 2 : level <= 5 ? int(rng, 2, 3) : int(rng, 3, 4);
    const nums: number[] = [];
    const ops: ('+' | '-' | '*' | '/')[] = [];
    for (let i = 0; i < terms; i++) nums.push(int(rng, 1, r.max));
    for (let i = 0; i < terms - 1; i++) ops.push(pick(rng, r.ops));
    // make division exact and subtraction non-negative where it is the last op
    const toks: Tok[] = [];
    let useParens = r.parens && terms >= 3 && rng() < 0.5;
    for (let i = 0; i < terms; i++) {
      if (useParens && i === 0) toks.push({ t: '(' });
      toks.push({ t: 'n', v: nums[i] });
      if (useParens && i === 1) toks.push({ t: ')' });
      if (i < terms - 1) toks.push({ t: 'op', v: ops[i] });
    }
    // x gem: replace one number with x and give a clue equation
    let x: Jar['x'];
    if (r.x && rng() < 0.6) {
      const idx = nums.findIndex(() => true);
      const xv = nums[idx];
      const k = int(rng, 1, 9);
      x = { value: xv, clue: rng() < 0.5 ? `x + ${k} = ${xv + k}` : `x − ${k} = ${xv - k}` };
      // replace first number token with x
      const ti = toks.findIndex(t => t.t === 'n');
      toks[ti] = { t: 'x' };
    }
    const res = evaluate(toks, x?.value);
    if (!res.ok) continue;
    if (res.value < 0 || res.value > r.targetMax || !Number.isInteger(res.value)) continue;
    if (res.value === 0 && (level < 7 || rng() < 0.8)) continue;
    // jar numbers: the solution's numbers (minus x) plus distractors
    const solutionNums = toks.filter(t => t.t === 'n').map(t => (t as any).v as number);
    const jar = solutionNums.slice();
    while (jar.length < r.numCount) jar.push(int(rng, 1, r.max));
    const minGems = solutionNums.length + (x ? 1 : 0);
    return {
      numbers: shuffle(rng, jar),
      ops: r.ops,
      parens: r.parens,
      x,
      target: res.value,
      solution: toks,
      minGems,
    };
  }
  // fallback (never expected): 2 + 3
  return { numbers: [2, 3, 5, 1, 4, 6], ops: ['+', '-'], parens: false, target: 5, solution: [{ t: 'n', v: 2 }, { t: 'op', v: '+' }, { t: 'n', v: 3 }], minGems: 2 };
}

/** Gems paid for a correct build: more numbers used pays more. */
export function buildBonus(numberGemsUsed: number): number {
  return Math.max(0, numberGemsUsed - 2);
}
